import * as React from "react";

// Breakpoints matching common device sizes
const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

/**
 * Detect if the device is likely a touch device (mobile or tablet)
 * This runs immediately without waiting for useEffect
 */
function getInitialDeviceType(): DeviceType {
  if (typeof window === 'undefined') return 'desktop';
  
  const width = window.innerWidth;
  
  // Check for touch capability as a secondary signal
  const hasTouch = 'ontouchstart' in window || 
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore - for older browsers
    navigator.msMaxTouchPoints > 0;
  
  // Check user agent for mobile/tablet indicators
  const ua = navigator.userAgent.toLowerCase();
  const isMobileUA = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);
  const isTabletUA = /ipad|tablet|playbook|silk/i.test(ua) || 
    (ua.includes('android') && !ua.includes('mobile'));
  
  // Samsung devices often report larger widths due to high DPI
  // Use a combination of signals
  if (width < MOBILE_BREAKPOINT || (isMobileUA && !isTabletUA)) {
    return 'mobile';
  }
  
  if (width < TABLET_BREAKPOINT || isTabletUA || (hasTouch && width < 1200)) {
    return 'tablet';
  }
  
  return 'desktop';
}

/**
 * Get device type based on viewport width
 */
function getDeviceTypeFromWidth(width: number): DeviceType {
  if (width < MOBILE_BREAKPOINT) return 'mobile';
  if (width < TABLET_BREAKPOINT) return 'tablet';
  return 'desktop';
}

/**
 * Hook to detect device type (mobile, tablet, desktop)
 * Uses multiple signals: viewport width, touch capability, user agent
 */
export function useDeviceType(): DeviceType {
  // Initialize with best guess immediately to avoid flash
  const [deviceType, setDeviceType] = React.useState<DeviceType>(getInitialDeviceType);

  React.useEffect(() => {
    const updateDeviceType = () => {
      const width = window.innerWidth;
      const newType = getDeviceTypeFromWidth(width);
      
      // For touch devices, prefer mobile/tablet classification even at larger widths
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const ua = navigator.userAgent.toLowerCase();
      const isMobileUA = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);
      
      // If it's a touch device with mobile UA but showing desktop width, 
      // it might be landscape mode on a phone - treat as mobile
      if (hasTouch && isMobileUA && width < 1200) {
        setDeviceType(width < TABLET_BREAKPOINT ? 'mobile' : 'tablet');
        return;
      }
      
      setDeviceType(newType);
    };

    // Run immediately
    updateDeviceType();

    // Listen for resize and orientation changes
    window.addEventListener('resize', updateDeviceType);
    window.addEventListener('orientationchange', updateDeviceType);
    
    // Also check on visibility change (handles tab switching)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        updateDeviceType();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('resize', updateDeviceType);
      window.removeEventListener('orientationchange', updateDeviceType);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return deviceType;
}

/**
 * Legacy hook for backwards compatibility
 * Now properly initializes to avoid flash of desktop on mobile
 */
export function useIsMobile(): boolean {
  const deviceType = useDeviceType();
  return deviceType === 'mobile';
}

/**
 * Hook to check if device is mobile or tablet (touch-first UI)
 */
export function useIsTouchDevice(): boolean {
  const deviceType = useDeviceType();
  return deviceType === 'mobile' || deviceType === 'tablet';
}

/**
 * Hook to check if device is tablet specifically
 */
export function useIsTablet(): boolean {
  const deviceType = useDeviceType();
  return deviceType === 'tablet';
}
